import React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Tooltip from '@mui/material/Tooltip';

import { IconButton } from 'components/Button';
import { StyledTextField } from 'components/Input/styles';

import { LibraryCategory } from 'utils/api/modules/entities/entities.types';

import { CategoryItem, CategoryLine, DeleteSlot, UsedByList, UsedByNote } from './styles';

const LIST_AT_MOST = 10;

export type CategoryDraft = {
  id: string;
  name: string;
  category?: LibraryCategory;
  removed?: boolean;
};

type CategoryRowProps = {
  draft: CategoryDraft;
  noun: string;
  error?: string;
  showUsedBy: boolean;
  autoFocus?: boolean;
  onChange: (id: string, name: string) => void;
  onDelete: (id: string) => void;
};

const usedByText = (category: LibraryCategory, noun: string): string =>
  `Used by ${category.usedByCount} ${category.usedByCount === 1 ? noun.replace(/s$/, '') : noun}`;

export const UsedBy: React.FunctionComponent<{ category: LibraryCategory; noun: string }> = ({ category, noun }) => (
  <UsedByList>
    <span>{`${usedByText(category, noun)}:`}</span>
    <ul>
      {category.usedBy.slice(0, LIST_AT_MOST).map(({ uuid, name }) => (
        <li key={uuid}>{name}</li>
      ))}
    </ul>
    {category.usedByCount > LIST_AT_MOST && <span>{`and ${category.usedByCount - LIST_AT_MOST} more`}</span>}
  </UsedByList>
);

const CategoryRow: React.FunctionComponent<CategoryRowProps> = ({
  draft,
  noun,
  error,
  showUsedBy,
  autoFocus,
  onChange,
  onDelete,
}) => {
  const { category } = draft;
  const inUse = Boolean(category && category.usedByCount > 0);
  const deleteLabel = `Delete ${draft.name || 'category'}`;

  return (
    <CategoryItem data-testid={`category-row-${draft.id}`}>
      <CategoryLine>
        <StyledTextField
          value={draft.name}
          size="small"
          autoFocus={autoFocus}
          onChange={(event) => onChange(draft.id, event.target.value)}
          error={Boolean(error)}
          helperText={error}
          inputProps={{ 'aria-label': 'Category name', 'data-testid': 'category-name-input' }}
        />
        <Tooltip title={category && inUse ? <UsedBy category={category} noun={noun} /> : deleteLabel}>
          <DeleteSlot
            tabIndex={inUse ? 0 : -1}
            aria-description={category && inUse ? usedByText(category, noun) : undefined}
          >
            <IconButton
              aria-label={deleteLabel}
              data-testid={`category-delete-${draft.id}`}
              disabled={inUse}
              onClick={() => onDelete(draft.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </DeleteSlot>
        </Tooltip>
      </CategoryLine>
      {showUsedBy && category && inUse && (
        <UsedByNote>
          <UsedBy category={category} noun={noun} />
        </UsedByNote>
      )}
    </CategoryItem>
  );
};

export default CategoryRow;
